import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import { makePatch } from '../../../../../../api/ajax';

import { constants as imagesConstants } from '../../../../../../redux/images';
import { constants as workflowConstants, actions as workflowsActions } from '../../../../../../redux/workflows';

import { Card } from '../../../../../../components';

import ImageQualityValidationSettings from '../ImageQualityValidationSettings/ImageQualityValidationSettings';
import SmartScanCommentSettings from '../SmartScanCommentSettings/SmartScanCommentSettings';

import './sass/additional-image-type-modal.scss';

/* Requested image type option */
const REQUESTED_IMAGE_OPTION = {
  OFF: 0,
  MANDATORY: 1,
  SKIPPABLE: 2
};

const AdditionalImageTypeModal = ({
  values, setFieldValue, workflow, imageTypeErrors,
  setErrorMessage, setHasUnsavedChanges
}) => {
  const dispatch = useDispatch();
  const firstCloseUp = imagesConstants.IMAGE_SUB_TYPES.FIRST_CLOSE_UP;
  const secondCloseUp = imagesConstants.IMAGE_SUB_TYPES.SECOND_CLOSE_UP;
  const manualDamageLabellingEnabled = workflow.damageMarkingType === workflowConstants.DAMAGE_MARKING_TYPE.MANUAL;
  const [additionalImages, setAdditionalImages] = useState(workflow.additionalCloseUpsOnly ? 0 : 1);

  const showDamageTypeMismatchError = imageTypeErrors.length > 0
    && imageTypeErrors.includes(imagesConstants.IMAGE_VERIFICATION_ERROR.DAMAGE_TYPE_MISMATCH);

  const showDamageNotFoundError = imageTypeErrors.length > 0
    && imageTypeErrors.includes(imagesConstants.IMAGE_VERIFICATION_ERROR.REPAIRABLE_DAMAGE_NOT_FOUND);

  const showDamageTooFarError = imageTypeErrors.length > 0
    && imageTypeErrors.includes(imagesConstants.IMAGE_VERIFICATION_ERROR.DAMAGE_TOO_FAR);

  const showDamageBlurryError = imageTypeErrors.length > 0
    && imageTypeErrors.includes(imagesConstants.IMAGE_VERIFICATION_ERROR.DAMAGE_BLURRY);

  const automatedWindshieldDamageDetection = workflow.damageMarkingType === workflowConstants.DAMAGE_MARKING_TYPE.MANUAL
                                  && workflow.damageTypeSelectionType === workflowConstants.DAMAGE_TYPE_SELECTION_TYPE.AI;

  const handleRequestedImageTypeOptionChange = (closeupType, value) => {
    if (value !== REQUESTED_IMAGE_OPTION.OFF) {
      // Check if we have configuration for given sub type
      let subTypeConfiguration = values.imageSubTypes.find((configuration) => configuration.imageSubType === closeupType);
      if (!subTypeConfiguration) {
        // If not, add it
        subTypeConfiguration = {
          imageSubType: closeupType,
          instructions: [],
          optional: true
        };
      }

      subTypeConfiguration.optional = value === REQUESTED_IMAGE_OPTION.SKIPPABLE;

      setFieldValue('imageSubTypes', [
        ...values.imageSubTypes.filter((configuration) => configuration.imageSubType !== value),
        subTypeConfiguration
      ]);
    } else {
      setFieldValue(
        'imageSubTypes',
        closeupType === firstCloseUp
          ? []
          : values.imageSubTypes.filter((configuration) => configuration.imageSubType !== secondCloseUp)
      );
    }
  };

  const requestedImageTypeOption = (closeupType) => {
    const imageSubTypeConfiguration = values.imageSubTypes.find((configuration) => configuration.imageSubType === closeupType);
    if (!imageSubTypeConfiguration) return REQUESTED_IMAGE_OPTION.OFF;

    return imageSubTypeConfiguration.optional ? REQUESTED_IMAGE_OPTION.SKIPPABLE : REQUESTED_IMAGE_OPTION.MANDATORY;
  };

  const handleError = (err) => {
    const { status, response: { error } } = err;
    switch (status) {
      case 400:
        setErrorMessage(error);
        break;
      default:
        setErrorMessage(null);
        break;
    }
  };

  const updateAdditionalCloseUpsOnly = (value) => {
    const additionalOnly = value !== 0;
    const additionalCloseUpsOnly = !additionalOnly;

    setErrorMessage(null);

    makePatch(`workflows/${workflow.id}`, { additionalCloseUpsOnly }).subscribe(
      () => {
        dispatch(workflowsActions.updateAdditionalCloseUpsOnlySuccess(workflow.id, additionalCloseUpsOnly));
        setHasUnsavedChanges(false);
      },
      (err) => {
        handleError(err);
      }
    );
  };

  return (
    <div className="additional-image-type-modal">
      {
        (manualDamageLabellingEnabled || automatedWindshieldDamageDetection) && (
          <>
            <div className="row mb-3">
              <div className="col-sm-12">
                <Card>
                  <Card.Header>Requested images</Card.Header>
                  <Card.Body>
                    <div className="row">
                      <div className="col-sm-6">
                        <div className="form-group">
                          <label htmlFor="asset" className="mb-0">
                            Closeup image 1
                          </label>
                          <select
                            className="form-control"
                            value={requestedImageTypeOption(firstCloseUp)}
                            onChange={(e) => {
                              handleRequestedImageTypeOptionChange(firstCloseUp, Number(e.target.value));
                            }}
                            name="asset"
                            id="asset"
                          >
                            <option value={REQUESTED_IMAGE_OPTION.OFF}>Off</option>
                            <option value={REQUESTED_IMAGE_OPTION.SKIPPABLE}>Skippable</option>
                            <option value={REQUESTED_IMAGE_OPTION.MANDATORY}>Mandatory</option>
                          </select>
                        </div>
                      </div>
                      <div className="col-sm-6">
                        <div className="form-group">
                          <label htmlFor="asset" className="mb-0">
                            Closeup image 2
                          </label>
                          <select
                            className="form-control"
                            value={requestedImageTypeOption(secondCloseUp)}
                            onChange={(e) => {
                              handleRequestedImageTypeOptionChange(secondCloseUp, Number(e.target.value));
                            }}
                            name="asset"
                            id="asset"
                            disabled={
                              !values.imageSubTypes.some((configuration) => configuration.imageSubType === firstCloseUp)
                            }
                          >
                            <option value={REQUESTED_IMAGE_OPTION.OFF}>Off</option>
                            <option value={REQUESTED_IMAGE_OPTION.SKIPPABLE}>Skippable</option>
                            <option value={REQUESTED_IMAGE_OPTION.MANDATORY}>Mandatory</option>
                          </select>
                        </div>
                      </div>
                    </div>
                    <div className="row">
                      <div className="col-sm-6">
                        <div className="form-group">
                          <label htmlFor="asset" className="mb-0">
                            Additional images
                          </label>
                          <select
                            className="form-control"
                            value={additionalImages}
                            onChange={(e) => {
                              const value = Number(e.target.value);
                              setAdditionalImages(value);
                              updateAdditionalCloseUpsOnly(value);
                            }}
                            name="asset"
                            id="asset"
                          >
                            <option value={1}>On</option>
                            <option value={0}>Off</option>
                          </select>
                        </div>
                      </div>
                    </div>
                  </Card.Body>
                </Card>
              </div>
            </div>
            <div className="row mt-3 mb-3">
              <div className="col-sm-12">
                <Card>
                  <Card.Header>Validations</Card.Header>
                  <Card.Body>
                    <div className="row mt-2">
                      <div className="col-sm-12 sub-section">
                        <span className="sub-section-header">IMAGE QUALITY</span>
                      </div>
                    </div>
                    <div className="row mt-2">
                      {
                        showDamageTypeMismatchError && (
                          <div className="col-sm-6">
                            <ImageQualityValidationSettings
                              error={imagesConstants.IMAGE_VERIFICATION_ERROR.DAMAGE_TYPE_MISMATCH}
                              values={values}
                              setFieldValue={setFieldValue}
                            />
                          </div>
                        )
                      }
                      {
                        showDamageNotFoundError && (
                          <div className="col-sm-6">
                            <ImageQualityValidationSettings
                              error={imagesConstants.IMAGE_VERIFICATION_ERROR.REPAIRABLE_DAMAGE_NOT_FOUND}
                              values={values}
                              setFieldValue={setFieldValue}
                            />
                          </div>
                        )
                      }
                    </div>
                    <div className="row">
                      {
                        showDamageBlurryError && (
                          <div className="col-sm-6">
                            <ImageQualityValidationSettings
                              error={imagesConstants.IMAGE_VERIFICATION_ERROR.DAMAGE_BLURRY}
                              values={values}
                              setFieldValue={setFieldValue}
                            />
                          </div>
                        )
                      }
                      {
                        showDamageTooFarError && (
                          <div className="col-sm-6">
                            <ImageQualityValidationSettings
                              error={imagesConstants.IMAGE_VERIFICATION_ERROR.DAMAGE_TOO_FAR}
                              values={values}
                              setFieldValue={setFieldValue}
                            />
                          </div>
                        )
                      }
                    </div>
                  </Card.Body>
                </Card>
              </div>
            </div>
          </>
        )
      }
      <div className="row">
        <div className="col-12">
          <Card>
            <Card.Header>
              Other settings
            </Card.Header>
            <Card.Body>
              <div className="row mt-2">
                <div className="col-sm-12 sub-section">
                  <span className="sub-section-header">COMMENTS</span>
                </div>
              </div>
              <div className="row mt-2">
                <div className="col-sm-12 smart-scan-comment-settings">
                  <SmartScanCommentSettings values={values} setFieldValue={setFieldValue} />
                </div>
              </div>
              {
                manualDamageLabellingEnabled && (
                  <>
                    <div className="row">
                      <div className="col-sm-12">
                        <hr />
                      </div>
                    </div>
                    <div className="row mt-2">
                      <div className="col-sm-12 sub-section">
                        <span className="sub-section-header">DAMAGE DETECTION</span>
                      </div>
                    </div>
                    <div className="row mt-2">
                      <div className="col-sm-6">
                        <div className="form-group">
                          <label htmlFor="asset" className="mb-0">
                            Manual marking
                          </label>
                          <select
                            className="form-control"
                            value={values.manualDamageLabelling.damageMarkingLevel}
                            onChange={(e) => {
                              setFieldValue('manualDamageLabelling.damageMarkingLevel', Number(e.target.value));
                            }}
                            name="asset"
                            id="asset"
                          >
                            <option value={workflowConstants.DAMAGE_MARKING_LEVEL.HIDDEN}>Hidden</option>
                            <option value={workflowConstants.DAMAGE_MARKING_LEVEL.WARNING}>Warning</option>
                            <option value={workflowConstants.DAMAGE_MARKING_LEVEL.BLOCKER}>Blocker</option>
                          </select>
                        </div>
                      </div>
                    </div>
                  </>
                )
              }
            </Card.Body>
          </Card>
        </div>
      </div>
    </div>
  );
};

AdditionalImageTypeModal.propTypes = {
  workflow: PropTypes.object.isRequired,
  values: PropTypes.object.isRequired,
  setFieldValue: PropTypes.func.isRequired,
  imageTypeErrors: PropTypes.array.isRequired,
  setErrorMessage: PropTypes.func.isRequired,
  setHasUnsavedChanges: PropTypes.func.isRequired
};

export default AdditionalImageTypeModal;
